import { AHButton, AHEmptyScreen, AHSeparator, Circle, ListItem } from "arkh";
import { StoreAdminNavigationScreenProps } from "./StoreAdminNavigation";
import { graphql, useLazyLoadQuery } from "react-relay";
import { CalendarsAdminListQuery } from "./__generated__/CalendarsAdminListQuery.graphql";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { ActionFooter } from "@/src/components/ActionFooter";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useLayoutEffect } from "react";
import { FlatList } from "react-native";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { SafeAreaView } from "react-native-safe-area-context";
import { FormattedMessage } from "react-intl";
import { Lottie } from "@/src/components/Lottie";

export function CalendarsAdmin(
  props: StoreAdminNavigationScreenProps<"CalendarsAdmin">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerCalendarsAdminList {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerCalendarsAdminList(
  props: {
    fetchKey: string;
  } & StoreAdminNavigationScreenProps<"CalendarsAdmin">
) {
  const intl_tokens = useIntlTokens();
  const data = useLazyLoadQuery<CalendarsAdminListQuery>(
    graphql`
      query CalendarsAdminListQuery {
        store {
          id
          calendars {
            __typename
            id
            title
            color
          }
          archivedCalendars {
            id
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions(
      getNavigationHeaderOptions({
        type: "BACK",
        onBack: () => {
          props.navigation.goBack();
        },
        headerTitle: intl_tokens.Calendars,
        more:
          data.store.archivedCalendars.length > 0
            ? [
                {
                  label: intl_tokens.ArchivedCalendars,
                  onPress: () => {
                    props.navigation.navigate("ArchivedCalendars");
                  },
                },
              ]
            : undefined,
      })
    );
  }, [
    getNavigationHeaderOptions,
    intl_tokens.ArchivedCalendars,
    intl_tokens.Calendars,
    props.navigation,
    data.store.archivedCalendars.length,
  ]);

  if (data.store.calendars.length === 0) {
    return (
      <SafeAreaView style={{ flex: 1 }}>
        <AHEmptyScreen
          title={
            <FormattedMessage
              description="Calendars Admin Empty Screen Title"
              defaultMessage="Keep track of events with the calendar!"
            />
          }
          subtitle={
            <FormattedMessage
              description="Calendars Admin Empty Screen Subtitle"
              defaultMessage={
                "Create your own calendar or subscribe to others to bring in important events worth keeping track of."
              }
            />
          }
          illustration={
            <Lottie
              autoPlay
              loop
              source={require("../../assets/shake-a-empty-box.json")}
            />
          }
          cta={
            <AHButton
              themeType="primary"
              iconType="calendar"
              iconRight
              title={intl_tokens.GetStarted}
              onPress={() => {
                props.navigation.navigate("CalendarCreate");
              }}
            />
          }
        />
      </SafeAreaView>
    );
  }

  return (
    <>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        data={data.store.calendars}
        renderItem={({ item }) => {
          return (
            <ListItem
              key={item.id}
              left={
                <Circle size="small" style={{ backgroundColor: item.color }} />
              }
              right={<ListItem.NavArrow />}
              onPress={() => {
                props.navigation.navigate("CalendarEdit", {
                  calendarId: item.id,
                });
              }}
            >
              <ListItem.Title>{item.title}</ListItem.Title>
            </ListItem>
          );
        }}
        keyExtractor={keyExtractorId}
      />
      <ActionFooter
        safeInsetBottom
        primary={
          <ActionFooter.Primary
            title={intl_tokens.AddCalendar}
            icon="calendar"
            onPress={() => {
              props.navigation.navigate("CalendarCreate");
            }}
          />
        }
      />
    </>
  );
}
